import { useId, useState, type ReactNode } from "react";
import {
  BookmarkSimpleIcon,
  CaretRightIcon,
  CheckIcon,
  DotsThreeIcon,
  PencilSimpleIcon,
  TrashIcon
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { SearchBar, SearchBarClear, SearchBarIcon, SearchBarInput } from "@libs/zapi-ui/ds/components/search-bar";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TProject } from "@/zapi-frontend/stores/project";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TSavedFilter } from "@/zapi-frontend/shared/types/savedFilters";
import type { TActiveFilters } from "../../lib/portfolioStorage";
import { distinctPeopleValues, distinctValuesForDimension } from "../../lib/usePortfolioDerivedList";
import {
  EMPTY_VALUE_TOKEN,
  OWNER_FILTER_KEY,
  STATUS_FILTER_KEY,
  STATUS_LABEL_KEYS,
  STATUS_OPTIONS,
  type TPortfolioStatus
} from "../../const/const";

const SAVED_FACET = "__saved__";

const statusDot: Record<TPortfolioStatus, string> = {
  Framing: "bg-muted-foreground/50",
  Generating: "bg-primary",
  Complete: "bg-success",
  Failed: "bg-destructive"
};

type TOption = { value: string; text: string; label: ReactNode };
// `testName` is the untranslated identity that keys the data-testids, matching DimensionChipsRow.
type TFacet = { key: string; testName: string; title: string; options: TOption[] };

type TProps = {
  dimensions: TOrganizationProjectDimension[];
  projects: TProject[];
  filters: TActiveFilters;
  onToggleFilterValue: (filterKey: string, value: string, checked: boolean) => void;
  onClearAll: () => void;
  savedFilters: TSavedFilter[];
  matchedSavedFilterId: number | undefined;
  onApplySavedFilter: (filter: TSavedFilter) => void;
  onRenameSavedFilter: (filter: TSavedFilter) => void;
  onDeleteSavedFilter: (filter: TSavedFilter) => void;
  onSaveCurrentFilters?: () => void;
};

export function FiltersPanel({
  dimensions,
  projects,
  filters,
  onToggleFilterValue,
  onClearAll,
  savedFilters,
  matchedSavedFilterId,
  onApplySavedFilter,
  onRenameSavedFilter,
  onDeleteSavedFilter,
  onSaveCurrentFilters
}: TProps) {
  const { t } = useTranslation("projects");
  const activeCount = Object.values(filters).reduce((n, s) => n + s.size, 0);
  const [activeKey, setActiveKey] = useState(STATUS_FILTER_KEY);

  const toOptions = (values: string[]): TOption[] =>
    values.map((value) =>
      value === EMPTY_VALUE_TOKEN
        ? {
            value,
            text: t("filters.notSet"),
            label: <span className="text-muted-foreground italic">{t("filters.notSet")}</span>
          }
        : { value, text: value, label: value }
    );

  const creatorValues = distinctPeopleValues(projects, "createdBy");
  const facets: TFacet[] = [
    {
      key: STATUS_FILTER_KEY,
      testName: "status",
      title: t("card.status"),
      options: STATUS_OPTIONS.map((status) => ({
        value: status,
        text: t(STATUS_LABEL_KEYS[status]),
        label: (
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", statusDot[status])} />
            {t(STATUS_LABEL_KEYS[status])}
          </span>
        )
      }))
    },
    ...(creatorValues.length > 0
      ? [{ key: OWNER_FILTER_KEY, testName: "Creator", title: t("card.creator"), options: toOptions(creatorValues) }]
      : []),
    ...dimensions.map((dimension) => {
      const key = String(dimension.id);
      return {
        key,
        testName: dimension.name,
        title: dimension.name,
        options: toOptions(distinctValuesForDimension(projects, key, dimension))
      };
    })
  ];

  const hasSaved = savedFilters.length > 0;
  const showingSaved = activeKey === SAVED_FACET && hasSaved;
  const activeFacet = facets.find((facet) => facet.key === activeKey) ?? facets[0];

  return (
    <div data-testid="projects-filter-row" className="flex h-[22rem] max-h-[calc(100dvh-8rem)] min-h-0">
      <nav aria-label={t("filters.heading")} className="flex w-44 shrink-0 flex-col gap-0.5 border-e p-2 sm:w-52">
        <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
          {facets.map((facet) => (
            <FacetButton
              key={facet.key}
              title={facet.title}
              count={filters[facet.key]?.size ?? 0}
              selected={!showingSaved && facet.key === activeFacet.key}
              testId={`filter-facet-${facet.testName}`}
              onSelect={() => setActiveKey(facet.key)}
            />
          ))}
          {hasSaved && (
            <>
              <Separator className="my-1.5" />
              <FacetButton
                title={t("savedFilters.heading")}
                icon={<BookmarkSimpleIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
                count={0}
                selected={showingSaved}
                testId="filter-facet-saved"
                onSelect={() => setActiveKey(SAVED_FACET)}
              />
            </>
          )}
        </div>
        <div className="flex flex-col items-start gap-0.5 pt-2">
          {onSaveCurrentFilters && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="save-current-filters"
              className="typo-label-md px-3"
              onClick={onSaveCurrentFilters}
            >
              <BookmarkSimpleIcon data-icon="inline-start" />
              {t("savedFilters.saveTitle")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            data-testid="filters-reset"
            disabled={activeCount === 0}
            onClick={onClearAll}
            className="typo-label-md px-3"
          >
            {t("filters.clearAll")}
          </Button>
        </div>
      </nav>
      {showingSaved ? (
        <SavedFiltersPane
          savedFilters={savedFilters}
          matchedId={matchedSavedFilterId}
          onApply={onApplySavedFilter}
          onRename={onRenameSavedFilter}
          onDelete={onDeleteSavedFilter}
        />
      ) : (
        <OptionsPane
          key={activeFacet.key}
          facet={activeFacet}
          selected={filters[activeFacet.key]}
          onToggle={(value, checked) => onToggleFilterValue(activeFacet.key, value, checked)}
        />
      )}
    </div>
  );
}

function FacetButton({
  title,
  icon,
  count,
  selected,
  testId,
  onSelect
}: {
  title: string;
  icon?: ReactNode;
  count: number;
  selected: boolean;
  testId: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={selected || undefined}
      data-testid={testId}
      onClick={onSelect}
      className="typo-label-md flex h-9 w-full shrink-0 items-center gap-2 rounded-lg px-3 text-start text-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 aria-current:bg-accent"
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {count > 0 ? (
        <Badge className="h-5 min-w-5 shrink-0 px-1.5 tabular-nums">{count}</Badge>
      ) : (
        <CaretRightIcon aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}

function PaneSearch({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) {
  const { t } = useTranslation("projects");
  return (
    <div className="p-2 pb-1">
      <SearchBar className="h-9 shadow-none">
        <SearchBarIcon />
        <SearchBarInput
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={label}
          aria-label={label}
        />
        {value && (
          <SearchBarClear className="me-1" aria-label={t("toolbar.clearSearch")} onClick={() => onChange("")} />
        )}
      </SearchBar>
    </div>
  );
}

function matches(text: string, query: string) {
  return text.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
}

function OptionsPane({
  facet,
  selected,
  onToggle
}: {
  facet: TFacet;
  selected: Set<string> | undefined;
  onToggle: (value: string, checked: boolean) => void;
}) {
  const { t } = useTranslation("projects");
  const id = useId();
  const [query, setQuery] = useState("");
  const shown = facet.options.filter((option) => matches(option.text, query));

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <PaneSearch value={query} onChange={setQuery} label={t("filters.searchIn", { name: facet.title })} />
      <div
        role="group"
        aria-label={facet.title}
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2 pt-1"
      >
        {shown.length === 0 ? (
          <p className="typo-body-sm px-2 py-6 text-center text-muted-foreground">
            {facet.options.length === 0 ? t("filters.noValues") : t("filters.noMatches")}
          </p>
        ) : (
          shown.map((option) => {
            const optionId = `${id}-${option.value}`;
            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className="typo-body-sm flex min-h-9 cursor-pointer items-center gap-3 rounded-lg px-2 text-foreground transition-colors hover:bg-accent"
              >
                <Checkbox
                  id={optionId}
                  checked={selected?.has(option.value) ?? false}
                  onCheckedChange={(checked) => onToggle(option.value, checked === true)}
                  data-testid={
                    facet.testName === "status"
                      ? `status-filter-${option.value}-checkbox`
                      : `dimension-filter-${facet.testName}-${option.value}-checkbox`
                  }
                />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function SavedFiltersPane({
  savedFilters,
  matchedId,
  onApply,
  onRename,
  onDelete
}: {
  savedFilters: TSavedFilter[];
  matchedId: number | undefined;
  onApply: (filter: TSavedFilter) => void;
  onRename: (filter: TSavedFilter) => void;
  onDelete: (filter: TSavedFilter) => void;
}) {
  const { t } = useTranslation("projects");
  const [query, setQuery] = useState("");
  const shown = savedFilters.filter((filter) => matches(filter.name, query));

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <PaneSearch
        value={query}
        onChange={setQuery}
        label={t("filters.searchIn", { name: t("savedFilters.heading") })}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2 pt-1">
        {shown.length === 0 ? (
          <p className="typo-body-sm px-2 py-6 text-center text-muted-foreground">{t("filters.noMatches")}</p>
        ) : (
          shown.map((filter) => (
            <div
              key={filter.id}
              data-testid="saved-filter-row"
              data-filter-id={filter.id}
              className="group/saved flex items-center gap-1 rounded-lg pe-1 transition-colors hover:bg-accent has-[[data-popup-open]]:bg-accent"
            >
              <button
                type="button"
                aria-pressed={matchedId === filter.id}
                data-testid={`saved-filter-${filter.id}-apply-button`}
                onClick={() => onApply(filter)}
                className="typo-body-sm flex min-h-9 min-w-0 flex-1 items-center gap-3 rounded-lg ps-2 text-start text-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {matchedId === filter.id && <CheckIcon aria-hidden="true" weight="bold" className="size-3.5" />}
                </span>
                <span className="min-w-0 flex-1 truncate" title={filter.name}>
                  {filter.name}
                </span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid={`saved-filter-${filter.id}-menu-trigger`}
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t("savedFilters.more")}
                      className="opacity-0 group-hover/saved:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100"
                    />
                  }
                >
                  <DotsThreeIcon weight="bold" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    data-testid={`saved-filter-${filter.id}-rename-item`}
                    onClick={() => onRename(filter)}
                  >
                    <PencilSimpleIcon />
                    {t("savedFilters.rename")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    data-testid={`saved-filter-${filter.id}-delete-item`}
                    onClick={() => onDelete(filter)}
                  >
                    <TrashIcon />
                    {t("savedFilters.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
